import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth/useAuth';
import type { Role } from '@/lib/auth/types';
import { RoleRank } from '@/lib/auth/types';

interface RequireAuthProps {
  minRole?: Role;
}

/**
 * Gate for protected routes. Waits out the initial session probe so a hard
 * refresh on an authenticated page doesn't flash the login screen. Redirects
 * unauthenticated callers to /login, and role-denied callers to /dashboard.
 */
export function RequireAuth({ minRole = 'Viewer' }: RequireAuthProps) {
  const { user, isAuthenticated, isBootstrapping } = useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-sm text-muted-foreground">
        Restoring session…
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (RoleRank[user.role] < RoleRank[minRole]) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
